import { axiosInstance } from "@/lib/axios-instance";
import type { Subscription, InvoicesResponse } from "@/api/billing-api";

export type OrgPlanKey = "free" | "plus" | "pro" | "elite";
export type OrgCadenceId = "monthly" | "quarterly" | "semi-annual" | "annual";

export interface OrgSubscriptionChangeResult {
  status: "active" | "scheduled" | "already_free" | "already_on_plan";
  from_plan: OrgPlanKey;
  to_plan: OrgPlanKey;
  effective_at?: string;
}

export interface OrgSubscriptionOverflowError {
  code: "premium_deals_exceed_cap" | "workspaces_exceed_cap";
  current: number;
  new_cap: number;
  remediation: string;
}

export interface OrgQuota {
  plan: OrgPlanKey;
  premium_deals_used: number;
  premium_deals_cap: number;
  // Plan-baseline cap plus add_on_credits_balance. Enforcement and the
  // sidebar/badge ceiling should both read this; the settings page still uses
  // premium_deals_cap so it can render the "X remaining + Y credits" breakdown.
  // Optional so a freshly-deployed FE can still render against an older BE
  // mid-rolling-deploy — call sites fall back to premium_deals_cap.
  effective_premium_deals_cap?: number;
  workspaces_used: number;
  workspaces_cap: number;
  add_on_credits_balance: number;
}

// A single org-wide premium (ai_tracked) deal, used to populate the downgrade
// "choose which deals to keep" modal.
export interface PremiumDeal {
  application_id: string;
  deal_name: string;
  website?: string;
  workspace_id: string;
  workspace_name: string;
  last_activity_at?: string;
}

export interface OrgPaymentMethod {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  holderName?: string;
}

export interface PaymentMethodSetupSession {
  checkout_url: string;
  session_id: string;
}

// Org-scoped billing endpoints (company-scoped subscription state).
// Mirrors the workspace-scoped BillingAPI shape so consumers can swap by
// changing the entry point only.
export const orgBillingApi = {
  async getSubscription(orgId: string): Promise<Subscription | null> {
    const response = await axiosInstance.get(
      `/api/org/${orgId}/subscription`
    );
    return response.data.result;
  },

  async getQuota(orgId: string): Promise<OrgQuota> {
    const response = await axiosInstance.get(`/api/org/${orgId}/quota`);
    return response.data.result;
  },

  async getPremiumDeals(orgId: string): Promise<PremiumDeal[]> {
    const response = await axiosInstance.get(
      `/api/org/${orgId}/premium-deals`
    );
    return response.data.result ?? [];
  },

  async changeSubscription(
    orgId: string,
    targetPlan: OrgPlanKey,
    targetCadence?: OrgCadenceId,
    keepApplicationIds?: string[],
    acknowledgeWorkspaceOverflow?: boolean
  ): Promise<OrgSubscriptionChangeResult> {
    const response = await axiosInstance.post(
      `/api/org/${orgId}/subscription/change`,
      {
        target_plan: targetPlan,
        target_cadence: targetCadence,
        keep_application_ids: keepApplicationIds,
        acknowledge_workspace_overflow: acknowledgeWorkspaceOverflow,
      }
    );
    return response.data;
  },

  async getPaymentMethod(orgId: string): Promise<OrgPaymentMethod | null> {
    const response = await axiosInstance.get(
      `/api/org/${orgId}/payment-method`
    );
    return response.data.result;
  },

  async startPaymentMethodSetup(
    orgId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<PaymentMethodSetupSession> {
    const response = await axiosInstance.post(
      `/api/org/${orgId}/payment-method/setup`,
      { success_url: successUrl, cancel_url: cancelUrl }
    );
    return response.data.result;
  },

  async confirmPaymentMethodSetup(
    orgId: string,
    sessionId: string
  ): Promise<{ payment_method_id: string }> {
    const response = await axiosInstance.post(
      `/api/org/${orgId}/payment-method/confirm`,
      { session_id: sessionId }
    );
    return response.data.result;
  },

  async startSubscriptionCheckout(
    orgId: string,
    targetPlan: Exclude<OrgPlanKey, "free">,
    targetCadence: OrgCadenceId,
    successUrl: string,
    cancelUrl: string
  ): Promise<PaymentMethodSetupSession> {
    const response = await axiosInstance.post(
      `/api/org/${orgId}/subscription/checkout`,
      {
        target_plan: targetPlan,
        target_cadence: targetCadence,
        success_url: successUrl,
        cancel_url: cancelUrl,
      }
    );
    return response.data.result;
  },

  async startCreditPackCheckout(
    orgId: string,
    packSize: number,
    successUrl: string,
    cancelUrl: string
  ): Promise<PaymentMethodSetupSession> {
    const response = await axiosInstance.post(
      `/api/org/${orgId}/credits/purchase`,
      {
        pack_size: packSize,
        success_url: successUrl,
        cancel_url: cancelUrl,
      }
    );
    return response.data.result;
  },

  async confirmCreditPackPurchase(
    orgId: string,
    sessionId: string
  ): Promise<{ credited: boolean; pack_size?: number; balance: number }> {
    const response = await axiosInstance.post(
      `/api/org/${orgId}/credits/confirm`,
      { session_id: sessionId }
    );
    return response.data.result;
  },

  async getInvoices(
    orgId: string,
    startingAfter?: string
  ): Promise<InvoicesResponse> {
    const response = await axiosInstance.get(`/api/org/${orgId}/invoices`, {
      params: startingAfter ? { starting_after: startingAfter } : undefined,
    });
    return response.data.result;
  },
};
